import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import { ArrowRight, ChevronDown, Globe, Mail, MapPin, ArrowUpRight, ShieldCheck, Zap, ArrowRightLeft, Users, Code, TrendingUp, Heart, Building2, RefreshCw, Layers, Briefcase, LogOut, LogIn, Linkedin } from 'lucide-react';
import { auth, signInWithGoogle, logOut } from './firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { ALL_NEWS } from './data/newsData';
import { collection, onSnapshot, query, orderBy, doc } from 'firebase/firestore';
import { db } from './firebase';
import ErrorBoundary from './components/ErrorBoundary';
import { handleFirestoreError, OperationType } from './lib/firestoreErrorHandler';

const AdminDashboard = React.lazy(() => import('./components/AdminDashboard'));
const ContactForm = React.lazy(() => import('./components/ContactForm'));

import viktorImg from '../IMG/Viktor P2.jpg';
import mariaImg from '../IMG/Maria 2.jpg';
import veqLogo from '../IMG/logo_veq_darkgrey.png';
import airpelagoLogo from '../IMG/Airpelago.svg';
import airpelagoBg from '../IMG/Airpelago Background.jpg';
import belecoLogo from '../IMG/Beleco.svg';
import belecoBg from '../IMG/Beleco Background.webp';
import ecotypeLogo from '../IMG/Ecotype.svg';
import ecotypeBg from '../IMG/Ecotype Background.jpg';
import globheLogo from '../IMG/Globhe Logo.svg';
import globheBg from '../IMG/Globhe Background.webp';
import fundrellaLogo from '../IMG/Fundrella Logo .jpeg';
import fundrellaBg from '../IMG/Fundrella Background.jpeg';
import cleeanLogo from '../IMG/Cleean Logo.svg';
import cleeanBg from '../IMG/Cleean Background.jpg';
import maiLogo from '../IMG/Mai Logo.svg';
import maiBg from '../IMG/Mai background.jpg';
import vntrsLogo from '../IMG/vntrs logo.png';
import norbanLogo from '../IMG/norban logga.png';
import norbanBg from '../IMG/Norban Background.jpeg';
import nrlyzeLogo from '../IMG/Nrlyze Logo.webp';
import nrlyzeBg from '../IMG/Nrlyze Background.webp';
import vdLogo from '../IMG/VD logo.svg';
import vdBg from '../IMG/Virtual Days Background.png';
import vitalaLogo from '../IMG/Vitala.svg';
import vitalaBg from '../IMG/Vitala Background.webp';
import frontVideo from '../IMG/Front Video.mp4';
import backVideo from '../IMG/Back Video 2.mp4';
import mvFrontImg from '../IMG/M&V.jpg';

import accentureLogo from '../IMG/Accenture Logo.svg';
import bcgLogo from '../IMG/BCG Logo 2.png';
import bridgepointLogo from '../IMG/Bridgepoint logo.png';
import carnegieLogo from '../IMG/DNB_Carnegie_logo_2025.svg';
import einrideLogo from '../IMG/Einride logo.svg';
import ericssonLogo from '../IMG/Ericsson Logo.png';
import googleLogo from '../IMG/google logo.svg';
import grantThorntonLogo from '../IMG/Grant_Thornton_logo.svg';
import pwcLogo from '../IMG/PwC_2025_Logo.svg';
import trustlyLogo from '../IMG/Trustly_Logotype.svg';

const NewsPage = React.lazy(() => import('./components/NewsPage'));
const ArticlePage = React.lazy(() => import('./components/ArticlePage'));
const SustainabilityPage = React.lazy(() => import('./components/SustainabilityPage'));
const PortfolioPage = React.lazy(() => import('./components/PortfolioPage'));

// --- Data ---
const PORTFOLIO = [
  { name: 'Airpelago', category: 'Drone Inspections', year: '2021', image: airpelagoBg, logo: airpelagoLogo, logoClassName: 'h-6', url: 'https://www.airpelago.com' },
  { name: 'Beleco', category: 'Furniture Platform', year: '2022', image: belecoBg, logo: belecoLogo, logoClassName: 'h-5', url: 'https://www.beleco.com' },
  { name: 'Cleean', category: 'Drone Cleaning', year: '2026', image: cleeanBg, logo: cleeanLogo, logoClassName: 'h-6', url: 'https://cleean.se', needsBlend: true },
  { name: 'Ecotype', category: 'Forest Analytics', year: '2022', image: ecotypeBg, logo: ecotypeLogo, logoClassName: 'h-6', url: 'https://www.ecotype.se' },
  { name: 'Fundrella', category: 'Sustainable Finance', year: '2023', image: fundrellaBg, logo: fundrellaLogo, logoClassName: 'h-7 pb-0.5', url: 'https://www.fundrella.com', needsBlend: true },
  { name: 'Globhe', category: 'Aerial Intelligence', year: '2021', image: globheBg, logo: globheLogo, logoClassName: 'h-5', url: 'https://globhe.com' },
  { name: 'Mai', category: 'Recommerce', year: '2025', image: maiBg, logo: maiLogo, logoClassName: 'h-5', url: 'https://mairesale.com/en' },
  { name: 'Norban', category: 'Residential Platform', year: '2023', image: norbanBg, logo: norbanLogo, logoClassName: 'h-5', url: 'https://norban.se' },
  { name: 'Nrlyze', category: 'Energy Analytics', year: '2025', image: nrlyzeBg, logo: nrlyzeLogo, logoClassName: 'h-5', url: 'https://nrlyze.se' },
  { name: 'Virtual Days', category: 'Virtual Events', year: '2024', image: vdBg, logo: vdLogo, logoClassName: 'h-5', url: 'https://virtualdays.com' },
  { name: 'Vitala', category: 'Preventive Care', year: '2022', image: vitalaBg, logo: vitalaLogo, logoClassName: 'h-6', url: 'https://www.vitala.health' },
];

const PILLARS = [
  { title: 'Higher success rate', description: 'Proven business models with limited financing risk — a fundamentally different risk profile.', icon: ShieldCheck, iconName: 'ShieldCheck' },
  { title: 'Lower competition', description: 'We look for a different type of company than most early-stage investors, which drastically lowers competition.', icon: Zap, iconName: 'Zap' },
  { title: 'More exit routes', description: 'Our companies attract PE firms, strategic buyers, and larger growth investors.', icon: ArrowRightLeft, iconName: 'ArrowRightLeft' },
  { title: 'Operational support', description: 'Through VNTRS and 15 coaches — real hands-on value in product, growth, finance, and people.', icon: Users, iconName: 'Users' },
];

const TEAM = [
  {
    name: 'Viktor Gällström',
    role: 'Founding Partner',
    bio: 'Viktor is an experienced entrepreneur and investor having completed 50+ investments in early stage tech companies, and having co-founded 3 companies himself. Before turning entrepreneur he primarily worked as an advisor specialized in CDD & Strategy. Currently on the board of directors in Airpelago, Ecotype, Nrlyze, Norban and Virtual Days.',
    image: viktorImg,
    linkedin: 'https://se.linkedin.com/in/viktorgallstrom'
  },
  {
    name: 'Maria Ternstad',
    role: 'Partner',
    bio: 'Maria is an investment professional with +8 years of investment experience. Prior to VEQ, she worked as an investment manager at VNTRS and before that spent four years with Swedish venture and growth investor Alfvén & Didrikson. Currently on the board of directors in Cleean, Mai, GLOBHE, Vitala, Beleco and Fundrella.',
    image: mariaImg,
    linkedin: 'https://se.linkedin.com/in/maria-micaelsdotter-bergsten'
  }
];

const CRITERIA = [
  { title: 'Sweden only', description: 'Local network advantage and deep market knowledge.', icon: Globe, iconName: 'Globe' },
  { title: 'Tech enabled', description: 'Software or hardware with clear defensibility.', icon: Code, iconName: 'Code' },
  { title: 'Early commercial', description: 'Real customers, proven demand, early revenue.', icon: TrendingUp, iconName: 'TrendingUp' },
  { title: 'Positive impact', description: 'Durable value for people or planet.', icon: Heart, iconName: 'Heart' },
];

const THEMES = [
  { title: 'Ticket size', description: 'We invest with a clear ticket size strategy.', icon: LucideIcons.Coins, iconName: 'Coins' },
  { title: 'Startup revenue', description: 'Companies with early commercial traction.', icon: LucideIcons.BarChart3, iconName: 'BarChart3' },
  { title: 'Near profitability', description: 'A clear path to profitability.', icon: LucideIcons.Target, iconName: 'Target' },
];

const COACH_AREAS = [
  { title: 'Product', description: 'Build, design and iterate with experienced product and tech leads.', icon: Layers, iconName: 'Layers' },
  { title: 'Growth', description: 'Go-to-market playbooks and hands-on commercial support.', icon: TrendingUp, iconName: 'TrendingUp' },
  { title: 'Finance & Legal', description: 'Fundraising strategy to board governance.', icon: Briefcase, iconName: 'Briefcase' },
  { title: 'People', description: 'Attract top talent. Build culture from 5 to 50.', icon: Users, iconName: 'Users' },
];

const COACH_LOGOS = [
  { name: 'Accenture', logo: accentureLogo, logoClassName: 'h-5 md:h-6' },
  { name: 'BCG', logo: bcgLogo, logoClassName: 'h-5 md:h-6' },
  { name: 'Bridgepoint', logo: bridgepointLogo, logoClassName: 'h-5 md:h-6' },
  { name: 'DNB Carnegie', logo: carnegieLogo, logoClassName: 'h-5 md:h-6' },
  { name: 'Einride', logo: einrideLogo, logoClassName: 'h-5 md:h-6' },
  { name: 'Ericsson', logo: ericssonLogo, logoClassName: 'h-5 md:h-6' },
  { name: 'Google', logo: googleLogo, logoClassName: 'h-5 md:h-6' },
  { name: 'Grant Thornton', logo: grantThorntonLogo, logoClassName: 'h-5 md:h-6' },
  { name: 'PwC', logo: pwcLogo, logoClassName: 'h-5 md:h-6' },
  { name: 'Trustly', logo: trustlyLogo, logoClassName: 'h-5 md:h-6' },
  { name: 'VNTRS', logo: vntrsLogo, logoClassName: 'h-5 md:h-6' },
];

const getLogoStyles = (name: string) => {
  const cleanName = name.trim().toLowerCase();
  
  // Default values for standard balanced sizing
  let maxHeight = '1.35rem';
  let maxWidth = '80%';
  let scale = 1.0;
  
  if (cleanName === 'pwc') {
    maxHeight = '2.7rem'; 
    maxWidth = '95%';
    scale = 1.70;
  } else if (cleanName === 'bridgepoint') {
    maxHeight = '2.75rem'; 
    maxWidth = '95%';
    scale = 1.75;
  } else if (cleanName === 'vntrs') {
    maxHeight = '1.25rem'; 
    maxWidth = '75%';
    scale = 0.98;
  } else if (cleanName === 'accenture') {
    maxHeight = '1.3rem'; 
    maxWidth = '75%';
    scale = 1.05;
  } else if (cleanName === 'bcg') {
    maxHeight = '1.15rem'; 
    maxWidth = '65%';
    scale = 0.85;
  } else if (cleanName === 'google') {
    maxHeight = '1.5rem';
    maxWidth = '80%';
    scale = 1.15;
  } else if (cleanName === 'trustly') {
    maxHeight = '1.7rem';
    maxWidth = '85%';
    scale = 1.35;
  } else if (cleanName === 'einride') {
    maxHeight = '1.15rem';
    maxWidth = '70%';
    scale = 0.92;
  } else if (cleanName === 'dnb carnegie' || cleanName === 'carnegie') {
    maxHeight = '1.45rem';
    maxWidth = '85%';
    scale = 1.15;
  } else if (cleanName === 'ericsson') {
    maxHeight = '1.5rem';
    maxWidth = '85%';
    scale = 1.15;
  }
  
  return {
    maxHeight,
    maxWidth,
    transform: `scale(${scale})`,
  };
};

const VNTRS_STATS = [
  { value: '2016', label: 'Founded' },
  { value: '40+', label: 'Employees' },
  { value: '4', label: 'Countries' },
  { value: '30+', label: 'Investments' },
];

// --- Components ---

const resolveLucideIcon = (name: string | undefined): React.ComponentType<any> => {
  if (!name || typeof name !== 'string') return LucideIcons.HelpCircle;
  
  const cleanName = name.trim();
  if (!cleanName) return LucideIcons.HelpCircle;

  // 1. Try exact match
  if ((LucideIcons as any)[cleanName]) return (LucideIcons as any)[cleanName];
  
  // 2. Try PascalCase (kebab-case or snake_case to PascalCase)
  // handles: "message-square", "message_square", "messagesquare", "Message Square"
  const pascalName = cleanName
    .split(/[-_ ]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
  
  if ((LucideIcons as any)[pascalName]) return (LucideIcons as any)[pascalName];

  // 3. Try with "Icon" suffix (some tools export like this)
  const withIconSuffix = pascalName.endsWith('Icon') ? pascalName : `${pascalName}Icon`;
  if ((LucideIcons as any)[withIconSuffix]) return (LucideIcons as any)[withIconSuffix];
  
  // 4. Try lowercase match (some users might type all lowercase)
  const allIcons = Object.keys(LucideIcons);
  const found = allIcons.find(key => key.toLowerCase() === cleanName.toLowerCase() || key.toLowerCase() === pascalName.toLowerCase());
  if (found) return (LucideIcons as any)[found];

  return LucideIcons.HelpCircle;
};

const FadeIn = ({ children, delay = 0, className = "" }: { children: React.ReactNode, delay?: number, className?: string }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.8, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
    className={className}
  >
    {children}
  </motion.div>
);

const renderBioWithLinks = (text: string) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s\)]+)/g;
  const parts = text.split(urlRegex);
  return (
    <>
      {parts.map((part, index) => {
        if (part.match(urlRegex)) {
          let cleanUrl = part;
          let suffix = '';
          const trail = /[.,;:?!\s]+$/;
          const match = part.match(trail);
          if (match) {
            cleanUrl = part.replace(trail, '');
            suffix = match[0];
          }
          return (
            <React.Fragment key={index}>
              <a 
                href={cleanUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-brand-sap underline hover:text-brand-pea transition-colors font-semibold"
                onClick={(e) => e.stopPropagation()}
              >
                {cleanUrl}
              </a>
              {suffix}
            </React.Fragment>
          );
        }
        return part;
      })}
    </>
  );
};

export default function App() {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  );
}

function AppContent() {
  const [scrolled, setScrolled] = useState(false);
  const [showAllNews, setShowAllNews] = useState(false);
  const [showAllCompanies, setShowAllCompanies] = useState(false);
  const [showSustainability, setShowSustainability] = useState(false);
  const [showAdminNews, setShowAdminNews] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<any>(null);
  const [user, setUser] = useState<User | null>(null);
  const [firestoreNews, setFirestoreNews] = useState<any[]>([]);
  const [deletedNews, setDeletedNews] = useState<string[]>([]);
  const [firestoreInvestments, setFirestoreInvestments] = useState<any[]>([]);
  const [deletedInvestments, setDeletedInvestments] = useState<string[]>([]);
  const [firestoreTeam, setFirestoreTeam] = useState<any[]>([]);
  const [deletedTeam, setDeletedTeam] = useState<string[]>([]);
  const [firestoreThemes, setFirestoreThemes] = useState<any[]>([]);
  const [deletedThemes, setDeletedThemes] = useState<string[]>([]);
  
  const [firestoreCriteria, setFirestoreCriteria] = useState<any[]>([]);
  const [deletedCriteria, setDeletedCriteria] = useState<string[]>([]);
  
  const [firestoreCoaches, setFirestoreCoaches] = useState<any[]>([]);
  const [deletedCoaches, setDeletedCoaches] = useState<string[]>([]);

  const [firestorePillars, setFirestorePillars] = useState<any[]>([]);
  const [deletedPillars, setDeletedPillars] = useState<string[]>([]);

  const [firestoreCoachLogos, setFirestoreCoachLogos] = useState<any[]>([]);
  const [deletedCoachLogos, setDeletedCoachLogos] = useState<string[]>([]);

  const [firestoreFooterLinks, setFirestoreFooterLinks] = useState<any[]>([]);
  const [deletedFooterLinks, setDeletedFooterLinks] = useState<string[]>([]);
  
  const [siteContent, setSiteContent] = useState<any>(null);
  const [siteContentLoading, setSiteContentLoading] = useState(true);
  const [videoReady, setVideoReady] = useState(false);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [loadStartTime] = useState(() => Date.now());
  const [loginError, setLoginError] = useState<string | null>(null);
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

  const heroVideoRef = useRef<HTMLVideoElement>(null);
  const contactVideoRef = useRef<HTMLVideoElement>(null);

  const getUrl = (url?: string) => {
    if (!url) return 'https://veq.vc'; // Fallback to main site if missing
    let u = String(url).trim();
    if (!u || u === '#' || u === 'undefined' || u === 'null') return 'https://veq.vc';
    if (!u.startsWith('http')) {
      u = 'https://' + u;
    }
    return u;
  };

  const handleLogin = async () => {
    try {
      setLoginError(null);
      const user = await signInWithGoogle();
      if (user && user.email) {
        const email = user.email.toLowerCase().trim();
        // Allowed domains/emails - broader check to ensure .capital works
        const isAuthorized = email.endsWith('@veq.vc') || 
                            email.endsWith('@veq.capital') || 
                            email.endsWith('@vntrs.com') ||
                            ['ludwig@veq.vc', 'ludwig@veq.capital', 'viktor@veq.capital', 'maria@veq.capital'].includes(email);
        
        if (!isAuthorized) {
          await logOut();
          setLoginError(`Access denied: ${email} is not authorized.`);
        }
      }
    } catch (error: any) {
      console.error('Login error:', error);
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user' || error.message?.includes('popup-closed')) {
        setLoginError('Login interrupted. Please use the "Open in new tab" link below for a stable login.');
      } else {
        setLoginError(`Login failed: ${error.message || 'Unknown error'}`);
      }
    }
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const isAdminUser = user && (
    user.uid === 'ysrx8ICggugT7Bj9ge1YaW0eg183' || 
    ((user.email?.toLowerCase().endsWith('@veq.vc') || user.email?.toLowerCase().endsWith('@veq.capital') || user.email?.toLowerCase().endsWith('@vntrs.com')) && user.emailVerified)
  );

  useEffect(() => {
    const q = query(collection(db, 'news'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFirestoreNews(newsData);
    }, (err) => {
      console.error('News stream error', err);
      if (err.message?.includes('permission-denied') || err.message?.includes('Missing or insufficient permissions')) {
        // Only trigger diagnostic if user *should* be admin
        if (isAdminUser) handleFirestoreError(err, OperationType.LIST, 'news');
      }
    });
    return () => unsubscribe();
  }, [isAdminUser]);

  useEffect(() => {
    const q = query(collection(db, 'deleted_news'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setDeletedNews(snapshot.docs.map(doc => doc.id));
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'investments'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const invData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFirestoreInvestments(invData);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'deleted_investments'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setDeletedInvestments(snapshot.docs.map(doc => doc.id));
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'team'), orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const teamData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFirestoreTeam(teamData);
    }, (err) => {
      console.error('Team stream error', err);
      if (err.message?.includes('permission-denied') && isAdminUser) {
        handleFirestoreError(err, OperationType.LIST, 'team');
      }
    });
    return () => unsubscribe();
  }, [isAdminUser]);

  useEffect(() => {
    const q = query(collection(db, 'deleted_team'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setDeletedTeam(snapshot.docs.map(doc => doc.id));
    }, (err) => {
      console.error('Deleted team stream error', err);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'themes'), orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setFirestoreThemes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (err) => {
      console.error('Themes stream error', err);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'deleted_themes'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setDeletedThemes(snapshot.docs.map(doc => doc.id));
    }, (err) => {
      console.error('Deleted themes stream error', err);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'criteria'), orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setFirestoreCriteria(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (err) => {
      console.error('Criteria stream error', err);
      if (err.message?.includes('permission-denied') && isAdminUser) {
        handleFirestoreError(err, OperationType.LIST, 'criteria');
      }
    });
    return () => unsubscribe();
  }, [isAdminUser]);

  useEffect(() => {
    const q = query(collection(db, 'deleted_criteria'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setDeletedCriteria(snapshot.docs.map(doc => doc.id));
    }, (err) => {
      console.error('Deleted criteria stream error', err);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'coaches'), orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setFirestoreCoaches(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (err) => {
      console.error('Coaches stream error', err);
      if (err.message?.includes('permission-denied') && isAdminUser) {
        handleFirestoreError(err, OperationType.LIST, 'coaches');
      }
    });
    return () => unsubscribe();
  }, [isAdminUser]);

  useEffect(() => {
    const q = query(collection(db, 'deleted_coaches'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setDeletedCoaches(snapshot.docs.map(doc => doc.id));
    }, (err) => {
      console.error('Deleted coaches stream error', err);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'pillars'), orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setFirestorePillars(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (err) => {
      console.error('Pillars stream error', err);
      if (err.message?.includes('permission-denied') && isAdminUser) {
        handleFirestoreError(err, OperationType.LIST, 'pillars');
      }
    });
    return () => unsubscribe();
  }, [isAdminUser]);

  useEffect(() => {
    const q = query(collection(db, 'deleted_pillars'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setDeletedPillars(snapshot.docs.map(doc => doc.id));
    }, (err) => {
      console.error('Deleted pillars stream error', err);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'footer_links'), orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setFirestoreFooterLinks(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (err) => {
      console.error('Footer links stream error', err);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'deleted_footer_links'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setDeletedFooterLinks(snapshot.docs.map(doc => doc.id));
    }, (err) => {
      console.error('Deleted footer links stream error', err);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'site_content', 'main'), (docSnap) => {
      if (docSnap.exists()) {
        setSiteContent(docSnap.data());
      }
      setSiteContentLoading(false);
    }, (err) => {
      console.error('Site content stream error', err);
      setSiteContentLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'coach_logos'), orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setFirestoreCoachLogos(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (err) => {
      console.error('Coach logos stream error', err);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'deleted_coach_logos'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setDeletedCoachLogos(snapshot.docs.map(doc => doc.id));
    }, (err) => {
      console.error('Deleted coach logos stream error', err);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (siteContentLoading) return;

    // Display VEQ logo for at least 4800ms (4.8 seconds) to ensure all core assets, styles, and videos on mobile
    // are fully loaded and prepared, matching the polished feel of the desktop experience.
    const minLoadTime = 4800; 
    
    // Fallback cutoff if the background video is blocked by low-power modes or takes a while to download.
    const maxVideoWait = 3500; 
    const absoluteMaxTime = minLoadTime + maxVideoWait; 

    const elapsed = Date.now() - loadStartTime;

    if (videoReady) {
      // Once the video starts playing (time update triggered), wait until the minimum VEQ logo view time has elapsed.
      const remainingTime = Math.max(0, minLoadTime - elapsed);
      // Soft offset (350ms) to ensure animations are perfectly synced
      const timer = setTimeout(() => {
        setLoadingComplete(true);
      }, remainingTime + 350);
      return () => clearTimeout(timer);
    } else {
      // If video is still buffering or autoplay is blocked, wait until our absolute fallback boundary.
      const remainingToCutoff = Math.max(0, absoluteMaxTime - elapsed);
      const timer = setTimeout(() => {
        setLoadingComplete(true);
      }, remainingToCutoff);
      return () => clearTimeout(timer);
    }
  }, [siteContentLoading, videoReady, loadStartTime]);

  useEffect(() => {
    const playVideo = async () => {
      const video = heroVideoRef.current;
      if (video) {
        video.setAttribute('muted', 'true');
        video.setAttribute('playsinline', 'true');
        video.muted = true;
        try {
          await video.play();
        } catch (err) {
          console.warn('Hero video autoplay failed/blocked:', err);
        }
      }
    };
    playVideo();
  }, [siteContent?.heroMedia, frontVideo]);

  useEffect(() => {
    const playVideo = async () => {
      const video = contactVideoRef.current;
      if (video) {
        video.setAttribute('muted', 'true');
        video.setAttribute('playsinline', 'true');
        video.muted = true;
        try {
          await video.play();
        } catch (err) {
          console.warn('Contact video autoplay failed/blocked:', err);
        }
      }
    };
    playVideo();
  }, [siteContent?.contactBackgroundImage, backVideo]);

  const activeHardcodedNews = ALL_NEWS.filter(n => !deletedNews.includes(n.slug) && !firestoreNews.some(fn => fn.id === n.slug));
  const combinedNews = [...firestoreNews, ...activeHardcodedNews];
  const NEWS = combinedNews.slice(0, 3);

  const activeHardcodedInvestments = PORTFOLIO.filter(p => !deletedInvestments.includes(p.name) && !firestoreInvestments.some(f => f.id === p.name || f.name === p.name));
  const combinedInvestments = [...firestoreInvestments, ...activeHardcodedInvestments].sort((a, b) => a.name.localeCompare(b.name)).map(company => {
    const fallback = PORTFOLIO.find(p => p.name.toLowerCase() === (company.name || company.id || "").toLowerCase().trim());
    
    // Check if the image / logo in the database is an uploaded one (http/https)
    // rather than a relative path that may have a stale asset hash from an old build.
    const isImageUploaded = company.image && (company.image.startsWith('http://') || company.image.startsWith('https://'));
    const finalImage = isImageUploaded ? company.image : (fallback?.image || company.image);

    const isLogoUploaded = company.logo && (company.logo.startsWith('http://') || company.logo.startsWith('https://'));
    const finalLogo = isLogoUploaded ? company.logo : (fallback?.logo || company.logo);
    
    return {
      ...company,
      image: finalImage,
      logo: finalLogo,
      logoClassName: company.logoClassName || fallback?.logoClassName,
      needsBlend: company.needsBlend !== undefined ? company.needsBlend : fallback?.needsBlend
    };
  });

  const activeHardcodedTeam = TEAM.filter(t => !deletedTeam.includes(t.name) && !firestoreTeam.some(ft => (ft.id || "").toLowerCase() === t.name.toLowerCase() || (ft.name || "").toLowerCase() === t.name.toLowerCase()));
  const combinedTeam = [...activeHardcodedTeam, ...firestoreTeam].map(member => {
    const memberName = member.name || member.id || "";
    const fallback = TEAM.find(t => t.name.toLowerCase().includes(memberName.toLowerCase()) || memberName.toLowerCase().includes(t.name.toLowerCase()));
    
    // Check if image is an uploaded one. If not, use the ESM fallback.
    const isImageUploaded = member.image && (member.image.startsWith('http://') || member.image.startsWith('https://'));
    const finalImage = isImageUploaded ? member.image : (fallback?.image || member.image);
    
    // Check if bio is empty or just spaces (e.g. " ")
    const dbBio = (member.bio || "").trim();
    const finalBio = dbBio !== "" ? member.bio : (fallback?.bio || "");
    
    // Check if linkedin link is empty or just spaces
    const finalLinkedin = member.linkedin !== undefined ? member.linkedin : (fallback?.linkedin || "");
    
    return {
      ...member,
      image: finalImage,
      bio: finalBio,
      linkedin: finalLinkedin
    };
  });

  const activeHardcodedThemes = THEMES.filter(t => !deletedThemes.includes(t.title) && !firestoreThemes.some(ft => ft.id === t.title));
  const combinedThemes = [...firestoreThemes, ...activeHardcodedThemes].map(theme => {
    const fallback = THEMES.find(t => (t.title || "").toLowerCase() === (theme.title || theme.id || "").toLowerCase());
    
    const dbDesc = (theme.description || "").trim();
    const finalDesc = dbDesc !== "" ? theme.description : (fallback?.description || "");
    
    return {
      ...theme,
      description: finalDesc,
      icon: theme.icon || resolveLucideIcon(theme.iconName) || fallback?.icon,
      iconName: theme.iconName || fallback?.iconName
    };
  });

  const activeHardcodedCriteria = CRITERIA.filter(c => !deletedCriteria.includes(c.title) && !firestoreCriteria.some(fc => fc.id === c.title));
  const combinedCriteria = [...firestoreCriteria, ...activeHardcodedCriteria].map(item => {
    const fallback = CRITERIA.find(c => c.title.toLowerCase() === (item.title || item.id || "").toLowerCase());
    
    const dbDesc = (item.description || "").trim();
    const finalDesc = dbDesc !== "" ? item.description : (fallback?.description || "");
    
    return {
      ...item,
      description: finalDesc,
      icon: item.icon || resolveLucideIcon(item.iconName) || fallback?.icon,
      iconName: item.iconName || fallback?.iconName
    };
  });

  const activeHardcodedCoaches = COACH_AREAS.filter(c => !deletedCoaches.includes(c.title) && !firestoreCoaches.some(fc => fc.id === c.title));
  const combinedCoaches = [...firestoreCoaches, ...activeHardcodedCoaches].map(coach => {
    const fallback = COACH_AREAS.find(c => c.title.toLowerCase() === (coach.title || coach.id || "").toLowerCase());
    
    const dbDesc = (coach.description || "").trim();
    const finalDesc = dbDesc !== "" ? coach.description : (fallback?.description || "");
    
    return {
      ...coach,
      description: finalDesc,
      icon: coach.icon || resolveLucideIcon(coach.iconName) || fallback?.icon,
      iconName: coach.iconName || fallback?.iconName
    };
  });

  const activeHardcodedPillars = PILLARS.filter(p => !deletedPillars.includes(p.title) && !firestorePillars.some(fp => fp.id === p.title));
  const combinedPillars = [...firestorePillars, ...activeHardcodedPillars];

  const activeHardcodedCoachLogos = COACH_LOGOS.filter(l => 
    !deletedCoachLogos.includes(l.name) && 
    !firestoreCoachLogos.some(fl => fl.id.toLowerCase() === l.name.toLowerCase() || fl.name.toLowerCase() === l.name.toLowerCase())
  );
  const combinedCoachLogos = [...firestoreCoachLogos, ...activeHardcodedCoachLogos].sort((a, b) => a.name.localeCompare(b.name));

  const combinedFooterLinks = firestoreFooterLinks;

  const getStudioTitle = () => {
    const title = siteContent?.studioTitle || 'Powered by';
    if (title.toUpperCase().includes('VNTRS')) return 'Powered by';
    return title;
  };

  const currentVntrsStats = [
    { 
      label: (siteContent?.studioStat1_Label === 'Founded Companies' ? 'Founded' : (siteContent?.studioStat1_Label || 'Founded')), 
      value: (siteContent?.studioStat1_Value === '15' ? '2016' : (siteContent?.studioStat1_Value || '2016')) 
    },
    { 
      label: (siteContent?.studioStat2_Label === 'Investments' && siteContent?.studioStat2_Value === '50+' ? 'Employees' : (siteContent?.studioStat2_Label || 'Employees')), 
      value: (siteContent?.studioStat2_Value === '50+' && siteContent?.studioStat2_Label === 'Investments' ? '40+' : (siteContent?.studioStat2_Value || '40+')) 
    },
    { 
      label: (siteContent?.studioStat3_Label === 'Exits' ? 'Countries' : (siteContent?.studioStat3_Label || 'Countries')), 
      value: (siteContent?.studioStat3_Value === '10' ? '4' : (siteContent?.studioStat3_Value || '4')) 
    },
    { 
      label: (siteContent?.studioStat4_Label === 'Lp Investors' ? 'Investments' : (siteContent?.studioStat4_Label || 'Investments')), 
      value: (siteContent?.studioStat4_Value === '100+' ? '30+' : (siteContent?.studioStat4_Value || '30+')) 
    },
  ];



  if (selectedArticle) {
    return (
      <ArticlePage 
        article={selectedArticle} 
        onBack={() => {
          setSelectedArticle(null);
          setTimeout(() => {
            document.getElementById('news')?.scrollIntoView({ behavior: 'instant', block: 'start' });
          }, 0);
        }} 
        backLabel={siteContent?.backToNews}
        logo={siteContent?.logoHeader}
      />
    );
  }

  if (showSustainability) {
    return (
      <SustainabilityPage 
        onBack={() => setShowSustainability(false)} 
        content={{
          label: siteContent?.sustainabilityLabel,
          title: siteContent?.sustainabilityTitle,
          description: siteContent?.sustainabilityDescription,
          body: siteContent?.sustainabilityBody
        }}
      />
    );
  }

  if (showAllCompanies) {
    return (
      <PortfolioPage 
        onBack={() => setShowAllCompanies(false)} 
        portfolio={combinedInvestments} 
        content={{
          title: siteContent?.portfolioPageTitle,
          description: siteContent?.portfolioPageDescription,
          backLabel: siteContent?.backToHome,
          investedInPrefix: siteContent?.investedInPrefix
        }}
        logo={siteContent?.logoHeader}
      />
    );
  }

  if (showAllNews) {
    return (
      <NewsPage 
        onBack={() => setShowAllNews(false)} 
        onSelectArticle={setSelectedArticle}
        content={{
          title: siteContent?.newsPageTitle,
          description: siteContent?.newsPageDescription,
          backLabel: siteContent?.backToHome,
          readArticleLabel: siteContent?.readArticleLabel
        }}
        logo={siteContent?.logoHeader}
      />
    );
  }

  if (showAdminNews && user) {
    return (
      <AdminDashboard 
        user={user}
        isAdminUser={isAdminUser}
        onClose={() => setShowAdminNews(false)} 
        combinedInvestments={combinedInvestments} 
        siteContent={siteContent} 
        combinedTeam={combinedTeam} 
        combinedThemes={combinedThemes}
        combinedCriteria={combinedCriteria} 
        combinedCoaches={combinedCoaches} 
        combinedCoachLogos={combinedCoachLogos}
        combinedPillars={combinedPillars} 
        combinedFooterLinks={combinedFooterLinks}
      />
    );
  }

  const appTextSize = siteContent?.appearance?.textSize === 'small' ? 'text-size-small' : siteContent?.appearance?.textSize === 'large' ? 'text-size-large' : 'text-size-medium';

  const getThemeClass = (section: string, defaultClass: string) => {
    const theme = siteContent?.appearance?.[section] || 'default';
    switch(theme) {
      case 'light': return 'bg-white text-gray-900';
      case 'beige': return 'bg-bg-beige text-text-heading';
      case 'merino': return 'bg-bg-merino text-text-heading';
      case 'pearl': return 'bg-bg-pearl text-text-heading';
      case 'grey-green': return 'bg-bg-grey-green text-text-heading';
      case 'brand-sap': return 'bg-brand-sap text-white';
      case 'brand-tea': return 'bg-brand-tea text-brand-sap';
      case 'brand-pea': return 'bg-brand-pea text-white';
      case 'brand-granny': return 'bg-brand-granny-smith text-brand-sap';
      case 'dark': return 'bg-gray-900 text-white';
      case 'custom': return '';
      default: return defaultClass;
    }
  }

  const getThemeStyle = (section: string): React.CSSProperties => {
    const theme = siteContent?.appearance?.[section];
    if (theme === 'custom') {
      return {
        backgroundColor: siteContent?.appearance?.[`customBg_${section}`] || '#ffffff',
        color: siteContent?.appearance?.[`customText_${section}`] || '#000000',
      };
    }
    return {};
  }

  return (
    <div className={`min-h-screen bg-bg-beige text-text-body selection:bg-brand-tea selection:text-text-heading overflow-hidden ${appTextSize}`}>
      
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-500 bg-[#F9F7EA] border-b border-bg-grey-green/30 md:border-b-0 py-4 md:bg-transparent ${scrolled ? 'md:bg-[#F9F7EA]/90 md:backdrop-blur-lg md:border-b md:border-bg-grey-green md:py-4 md:shadow-sm' : 'md:py-6'}`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center">
          <a href="#" className="flex items-center mr-12" onClick={() => setMobileMenuOpen(false)}>
            <img src={siteContent?.logoHeader || veqLogo} alt="VEQ" className="h-8 w-auto object-contain" />
          </a>
          
          <div className="hidden md:flex space-x-8 text-sm font-medium text-text-body">
            <a href="#philosophy" className="hover:text-brand-sap transition-colors">{siteContent?.navPhilosophy || 'Philosophy'}</a>
            <a href="#investments" className="hover:text-brand-sap transition-colors">{siteContent?.navInvestments || 'Investments'}</a>
            <a href="#criteria" className="hover:text-brand-sap transition-colors">{siteContent?.navCriteria || 'Criteria'}</a>
            <a href="#team" className="hover:text-brand-sap transition-colors">{siteContent?.navTeam || 'Team'}</a>
            <a href="#coaches" className="hover:text-brand-sap transition-colors">{siteContent?.navCoaches || 'VEQ Coaches'}</a>
          </div>

          <div className="hidden md:flex items-center space-x-8 ml-auto text-sm font-medium text-text-body">
            <button 
              onClick={() => setShowContactForm(true)}
              className="inline-flex items-center justify-center text-brand-sap bg-brand-tea hover:bg-brand-granny-smith px-6 py-2.5 rounded-full font-medium transition-colors shadow-sm hover:shadow-md"
            >
              {siteContent?.navContact || 'Get in touch!'}
            </button>
            <a href="#news" className="hover:text-brand-sap transition-colors">{siteContent?.navNews || 'News'}</a>
            
            {user ? (
              <div className="flex items-center space-x-4">
                {isAdminUser && (
                  <button onClick={() => setShowAdminNews(true)} className="inline-flex items-center space-x-2 text-brand-sap bg-brand-tea hover:bg-brand-granny-smith px-4 py-2 rounded-full font-medium transition-colors">
                    <span>{siteContent?.adminDashboardLabel || 'Admin Dashboard'}</span>
                  </button>
                )}
                {!isAdminUser && user && (
                  <div className="text-xs text-red-500 max-w-[120px] text-right leading-tight">
                    {user.emailVerified ? 'Unrecognized admin email' : 'Verify your email to manage site'}
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  {user.photoURL ? (
                    <img src={user.photoURL} alt={user.displayName || "User"} className="w-8 h-8 rounded-full border border-brand-sap/20" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-brand-tea flex items-center justify-center text-brand-sap font-bold">
                      {(user.displayName || user.email || "?")[0].toUpperCase()}
                    </div>
                  )}
                </div>
                <button 
                  onClick={logOut}
                  className="inline-flex items-center space-x-1 text-text-body hover:text-brand-sap transition-colors"
                  title={siteContent?.logoutLabel || 'Log out'}
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                {loginError && (
                  <div className="flex flex-col items-end">
                    <span className="text-red-500 text-[10px] uppercase font-bold tracking-tight max-w-[150px] leading-tight text-right mb-1">{loginError}</span>
                    <a href={window.location.href} target="_blank" rel="noopener noreferrer" className="text-brand-sap text-[10px] font-bold underline flex items-center">
                      Open in new tab <ArrowUpRight className="w-2 h-2 ml-0.5" />
                    </a>
                  </div>
                )}
                <button 
                  onClick={handleLogin}
                  className="inline-flex items-center space-x-2 text-text-body hover:text-brand-sap transition-colors"
                >
                  <LogIn className="w-4 h-4" />
                  <span>{siteContent?.loginLabel || 'Log in'}</span>
                </button>
              </div>
            )}
          </div>

          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-text-heading ml-auto p-2"
          >
            {mobileMenuOpen ? (
              <LucideIcons.X className="w-6 h-6" />
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/></svg>
            )}
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        <motion.div
          initial={false}
          animate={mobileMenuOpen ? "open" : "closed"}
          variants={{
            open: { opacity: 1, x: 0, pointerEvents: 'auto' },
            closed: { opacity: 0, x: '100%', pointerEvents: 'none' }
          }}
          className="fixed inset-0 top-[73px] bg-[#F9F7EA] opacity-100 z-[99] md:hidden p-8 flex flex-col pt-12"
        >
          <div className="flex flex-col space-y-8 text-2xl font-heading text-text-heading">
            <a href="#philosophy" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-sap transition-colors">{siteContent?.navPhilosophy || 'Philosophy'}</a>
            <a href="#investments" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-sap transition-colors">{siteContent?.navInvestments || 'Investments'}</a>
            <a href="#criteria" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-sap transition-colors">{siteContent?.navCriteria || 'Criteria'}</a>
            <a href="#team" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-sap transition-colors">{siteContent?.navTeam || 'Team'}</a>
            <a href="#coaches" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-sap transition-colors">{siteContent?.navCoaches || 'VEQ Coaches'}</a>
            <a href="#news" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-sap transition-colors">{siteContent?.navNews || 'News'}</a>
          </div>

          <div className="mt-auto flex flex-col space-y-6 pt-12 border-t border-bg-grey-green">
            <button 
              onClick={() => { setShowContactForm(true); setMobileMenuOpen(false); }}
              className="inline-flex items-center justify-center text-white bg-brand-sap px-8 py-4 rounded-full font-medium shadow-lg"
            >
              {siteContent?.navContact || 'Get in touch!'}
            </button>
            
            <div className="flex items-center justify-between">
              {user ? (
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-3">
                    {user.photoURL ? (
                      <img src={user.photoURL} alt={user.displayName || "User"} className="w-10 h-10 rounded-full border border-brand-sap/20" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-brand-tea flex items-center justify-center text-brand-sap font-bold">
                        {(user.displayName || user.email || "?")[0].toUpperCase()}
                      </div>
                    )}
                    <div className="flex flex-col">
                      <span className="text-sm font-bold text-text-heading line-clamp-1">{user.displayName || user.email?.split('@')[0]}</span>
                      {isAdminUser && (
                        <button onClick={() => { setShowAdminNews(true); setMobileMenuOpen(false); }} className="text-xs text-brand-sap font-bold text-left underline">
                          {siteContent?.adminDashboardLabel || 'Admin Dashboard'}
                        </button>
                      )}
                    </div>
                  </div>
                  <button 
                    onClick={() => { logOut(); setMobileMenuOpen(false); }}
                    className="p-2 text-text-muted hover:text-brand-sap transition-colors bg-gray-100 rounded-full"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col space-y-2">
                  <button 
                    onClick={() => { handleLogin(); setMobileMenuOpen(false); }}
                    className="inline-flex items-center space-x-3 text-text-body hover:text-brand-sap transition-colors text-lg font-medium"
                  >
                    <LogIn className="w-6 h-6" />
                    <span>{siteContent?.loginLabel || 'Log in'}</span>
                  </button>
                  {loginError && (
                    <a href={window.location.href} target="_blank" rel="noopener noreferrer" className="text-brand-sap text-sm font-bold underline flex items-center ml-9">
                      Try opening in new tab <ArrowUpRight className="w-3 h-3 ml-1" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </nav>

      {/* Hero Section */}
      <section className={`relative min-h-screen flex items-center pt-20 ${videoReady ? getThemeClass('hero', 'bg-bg-pearl') : 'bg-[#EAE8E4]'}`} style={videoReady ? getThemeStyle('hero') : { backgroundColor: '#EAE8E4' }}>
        <div className="absolute inset-0 z-0 overflow-hidden bg-[#EAE8E4]">
          <div className="absolute inset-0">
            {/* Gradient overlay to ensure text readability */}
            <div className="absolute inset-0 bg-black/5 md:bg-transparent z-0" />
            <div className={`absolute inset-0 bg-bg-pearl/40 md:bg-gradient-to-r md:from-bg-pearl/70 md:via-bg-pearl/30 md:to-transparent z-10 transition-opacity duration-1000 ${videoReady ? 'opacity-100' : 'opacity-0'}`} />
            {siteContent?.heroMedia ? (
              siteContent.heroMedia.match(/\.(mp4|webm|mov|ogg)(\?.*)?$/i) || siteContent.heroMedia.includes('video/') ? (
                <video 
                  ref={heroVideoRef}
                  src={siteContent.heroMedia} 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  preload="auto"
                  onPlaying={() => setVideoReady(true)}
                  onTimeUpdate={() => setVideoReady(true)}
                  className={`w-full h-full object-cover object-center contrast-[1.05] brightness-[1.02] scale-100 transition-opacity duration-1000 ${videoReady ? 'opacity-100' : 'opacity-0'}`}
                />
              ) : (
                <img
                  src={siteContent.heroMedia}
                  alt="Hero Background"
                  onLoad={() => setVideoReady(true)}
                  className="w-full h-full object-cover object-center contrast-[1.05] brightness-[1.1]"
                  referrerPolicy="no-referrer"
                />
              )
            ) : (
              <video 
                ref={heroVideoRef}
                src={frontVideo} 
                autoPlay 
                loop 
                muted 
                playsInline
                preload="auto"
                onPlaying={() => setVideoReady(true)}
                onTimeUpdate={() => setVideoReady(true)}
                className={`w-full h-full object-cover object-center contrast-[1.05] brightness-[1.02] scale-100 transition-opacity duration-1000 ${videoReady ? 'opacity-100' : 'opacity-0'}`}
              />
            )}
            
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10 w-full">
          <div className="relative flex items-center min-h-[80vh] py-20">
            {/* Text Content - Higher z-index to sit above the video background */}
            <div className="max-w-3xl relative z-10">
              <FadeIn>
                <div className="inline-flex items-center space-x-2 border border-brand-granny-smith bg-brand-tea/30 rounded-full px-4 py-1.5 mb-8">
                  <span className="w-2 h-2 rounded-full bg-brand-sap animate-pulse" />
                  <span className="text-xs font-medium tracking-widest uppercase text-brand-sap">Stockholm, Sweden</span>
                </div>
              </FadeIn>
              
              <FadeIn delay={0.1}>
                <h1 
                  className="text-5xl md:text-6xl lg:text-7xl font-heading leading-[1.1] tracking-tight mb-8 text-balance text-text-heading drop-shadow-xl"
                  dangerouslySetInnerHTML={{ 
                    __html: (siteContent?.heroTitle || 'Providing the first <span class="text-brand-sap font-bold">growth capital</span> for Swedish technology companies.')
                  }}
                />
              </FadeIn>
              
              <FadeIn delay={0.15}>
                <p className="text-xl md:text-2xl text-text-heading max-w-2xl font-semibold leading-relaxed mb-12">
                  {siteContent?.heroSubtitle || 'We back tech-enabled companies in the early commercial phase — founders with real traction, durable economics, and the ambition to scale.'}
                </p>
              </FadeIn>
              
              <FadeIn delay={0.3} className="flex flex-wrap gap-4">
                <button 
                  onClick={() => setShowContactForm(true)}
                  className="inline-flex items-center justify-center space-x-3 text-brand-sap bg-brand-tea hover:bg-brand-granny-smith px-8 py-4 rounded-full font-medium transition-colors shadow-md group"
                >
                  <span>Get in touch!</span>
                  <Mail className="w-4 h-4 group-hover:scale-110 transition-transform" />
                </button>
                <a href="#investments" className="inline-flex items-center justify-center space-x-3 bg-white text-brand-sap border border-brand-sap/20 px-8 py-4 rounded-full font-medium hover:bg-brand-sap/5 transition-colors group">
                  <span>View Investments</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </FadeIn>
            </div>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce text-brand-sap/50">
          <ChevronDown className="w-6 h-6" />
        </div>
      </section>

      {/* Philosophy Section */}
      <section id="philosophy" className={`py-24 relative ${getThemeClass('philosophy', 'bg-bg-merino')}`} style={getThemeStyle('philosophy')}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mb-8 items-center">
            <div className="lg:col-span-5 flex flex-col">
              <FadeIn>
                <h2 className="text-sm font-bold tracking-widest uppercase text-brand-pea mb-4">{siteContent?.philosophyLabel || 'Philosophy'}</h2>
                <h3 className="text-4xl md:text-5xl font-heading leading-tight mb-6 text-text-heading">
                  {siteContent?.philosophyTitle || 'The opposite to traditional VC.'}
                </h3>
                <div className="space-y-4 text-text-body text-lg leading-relaxed">
                  <p>
                    {siteContent?.philosophyText1 || 'We fill a clear gap — companies with real traction, sustainable economics, and room to grow. Without forcing a classic venture narrative.'}
                  </p>
                  <p>
                    {siteContent?.philosophyText2 || 'We look for founders who show grit, intelligence, and maybe most of all — integrity and a moral compass pointing in the right direction.'}
                  </p>
                </div>
              </FadeIn>
            </div>
            
            <div className="lg:col-span-7">
              <FadeIn delay={0.2}>
                <div className="relative rounded-[2rem] overflow-hidden shadow-lg aspect-[4/3] md:aspect-[16/9] border border-brand-tea/30">
                  <img 
                    src={siteContent?.philosophyImage || mvFrontImg} 
                    alt="Philosophy" 
                    className="w-full h-full object-cover object-[30%_center]"
                    referrerPolicy="no-referrer"
                  />
                </div>
              </FadeIn>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {combinedPillars.map((pillar, idx) => (
              <FadeIn key={idx} delay={idx * 0.1} className="h-full">
                <div className="group bg-white p-6 rounded-2xl shadow-sm border border-brand-tea/20 h-full flex flex-col hover:shadow-md transition-all hover:-translate-y-1">
                  <div className="text-brand-sap/40 mb-6 group-hover:text-brand-sap transition-colors shrink-0">
                    {(() => {
                      const Icon = pillar.icon || resolveLucideIcon(pillar.iconName) || LucideIcons.HelpCircle;
                      return <Icon className="w-8 h-8 stroke-[1.5]" />;
                    })()}
                  </div>
                  <h4 className="text-xl font-medium mb-3 text-text-heading group-hover:text-brand-sap transition-colors">{pillar.title}</h4>
                  <p className="text-text-body leading-relaxed text-sm flex-grow">{pillar.description}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Investments Section */}
      <section id="investments" className={`py-24 relative overflow-hidden ${getThemeClass('investments', 'bg-bg-beige')}`} style={getThemeStyle('investments')}>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <FadeIn>
            <div className="flex justify-between items-end mb-16">
              <div>
                <h2 className="text-sm font-bold tracking-widest uppercase text-brand-pea mb-4">{siteContent?.investmentsLabel || 'Our Investments'}</h2>
                <h3 className="text-4xl md:text-5xl font-heading text-text-heading">{siteContent?.investmentsTitle || 'Selected Portfolio'}</h3>
              </div>
              <button onClick={() => setShowAllCompanies(true)} className="hidden md:flex items-center space-x-2 text-sm font-medium text-brand-sap bg-brand-tea hover:bg-brand-granny-smith px-6 py-3 rounded-full hover:shadow-md transition-all group">
                <span>{siteContent?.investmentsAllButton || 'View All Investments'}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </FadeIn>
        </div>

        {/* Infinite Marquee of Cards */}
        <div className="relative w-full overflow-hidden flex items-center py-4 text-brand-sap">
          <div className="flex animate-marquee w-max hover:[animation-play-state:paused] relative">
            {/* First set of cards */}
            <div className="flex items-center gap-6 px-3">
              {combinedInvestments.map((company, idx) => (
                <a 
                  href={getUrl(company.url)} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  title={`Visit ${company.name} website`}
                  key={`card-1-${company.name}-${idx}`} 
                  onPointerDown={(e) => e.stopPropagation()}
                  className="group relative w-72 md:w-80 aspect-[4/5] overflow-hidden bg-white rounded-2xl cursor-pointer shadow-sm hover:shadow-xl active:scale-[0.98] transition-all duration-500 flex flex-col border border-gray-100 flex-shrink-0 z-50 pointer-events-auto"
                >
                  <div className="relative flex-1 overflow-hidden bg-bg-grey-green">
                    <img 
                      src={company.image} 
                      alt={company.name} 
                      className={`absolute inset-0 w-full h-full opacity-80 group-hover:opacity-100 transition-all duration-700 group-hover:scale-105 pointer-events-none ${company.imageClassName || 'object-cover'}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500 pointer-events-none" />
                    
                    <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-start pointer-events-none">
                      <div className="flex justify-end items-start">
                        <ArrowUpRight className="w-5 h-5 text-white/0 group-hover:text-white/100 transition-all duration-300 -translate-x-2 translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="relative bg-white px-6 py-5 border-t border-gray-100 z-10 h-32 flex flex-col pointer-events-none">
                    <div className="flex-1 flex items-center justify-between">
                      {company.logo ? (
                        <img 
                          src={company.logo} 
                          alt={company.name} 
                          className={`${company.logoClassName || 'h-8'} object-contain ${company.needsBlend ? 'mix-blend-multiply' : ''}`} 
                        />
                      ) : (
                        <h4 className="text-2xl font-heading text-text-heading">{company.name}</h4>
                      )}
                      <div className="w-8 h-8 rounded-full bg-bg-grey-green flex items-center justify-center text-brand-sap group-hover:bg-brand-sap group-hover:text-white transition-all duration-300">
                        <ArrowUpRight className="w-4 h-4" />
                      </div>
                    </div>
                    <span className="text-[10px] font-bold tracking-widest uppercase text-gray-700 mt-2">
                      {company.category}
                    </span>
                  </div>
                </a>
              ))}
            </div>
            {/* Second set of cards for seamless loop */}
            <div className="flex items-center gap-6 px-3">
              {combinedInvestments.map((company, idx) => (
                <a 
                  href={getUrl(company.url)} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  title={`Visit ${company.name} website`}
                  key={`card-2-${company.name}-${idx}`} 
                  onPointerDown={(e) => e.stopPropagation()}
                  className="group relative w-72 md:w-80 aspect-[4/5] overflow-hidden bg-white rounded-2xl cursor-pointer shadow-sm hover:shadow-xl active:scale-[0.98] transition-all duration-500 flex flex-col border border-gray-100 flex-shrink-0 z-50 pointer-events-auto"
                >
                  <div className="relative flex-1 overflow-hidden bg-bg-grey-green">
                    <img 
                      src={company.image} 
                      alt={company.name} 
                      className={`absolute inset-0 w-full h-full opacity-80 group-hover:opacity-100 transition-all duration-700 group-hover:scale-105 pointer-events-none ${company.imageClassName || 'object-cover'}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500 pointer-events-none" />
                    
                    <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-start pointer-events-none">
                      <div className="flex justify-end items-start">
                        <ArrowUpRight className="w-5 h-5 text-white/0 group-hover:text-white/100 transition-all duration-300 -translate-x-2 translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="relative bg-white px-6 py-5 border-t border-gray-100 z-10 h-32 flex flex-col pointer-events-none">
                    <div className="flex-1 flex items-center justify-between">
                      {company.logo ? (
                        <img 
                          src={company.logo} 
                          alt={company.name} 
                          className={`${company.logoClassName || 'h-8'} object-contain ${company.needsBlend ? 'mix-blend-multiply' : ''}`} 
                        />
                      ) : (
                        <h4 className="text-2xl font-heading text-text-heading">{company.name}</h4>
                      )}
                      <div className="w-8 h-8 rounded-full bg-bg-grey-green flex items-center justify-center text-brand-sap group-hover:bg-brand-sap group-hover:text-white transition-all duration-300">
                        <ArrowUpRight className="w-4 h-4" />
                      </div>
                    </div>
                    <span className="text-[10px] font-bold tracking-widest uppercase text-gray-700 mt-2">
                      {company.category}
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 mt-12 text-center md:hidden relative z-10">
          <button onClick={() => setShowAllCompanies(true)} className="inline-flex items-center space-x-2 text-sm font-medium text-brand-sap bg-brand-tea hover:bg-brand-granny-smith px-6 py-3 rounded-full hover:shadow-md transition-all group">
            <span>{siteContent?.investmentsAllButton || 'View All Investments'}</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      {/* Criteria Section */}
      <section id="criteria" className={`py-24 relative ${getThemeClass('criteria', 'bg-white')}`} style={getThemeStyle('criteria')}>
        <div className="max-w-7xl mx-auto px-6">
          <FadeIn>
            <h2 className="text-sm font-bold tracking-widest uppercase text-brand-pea mb-4">{siteContent?.criteriaLabel || 'Criteria'}</h2>
            <h3 className="text-4xl md:text-5xl font-heading leading-tight mb-16 text-text-heading max-w-3xl">
              {siteContent?.criteriaTitle || 'Clear criteria. A selective lens'}
            </h3>
          </FadeIn>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {combinedCriteria.map((item, idx) => (
              <FadeIn key={idx} delay={idx * 0.1} className="h-full">
                <div className="bg-bg-merino p-8 rounded-2xl h-full flex flex-col border border-brand-tea/10 hover:shadow-md transition-shadow">
                  <div className="text-brand-sap/60 mb-4 shrink-0">
                    {(() => {
                      const Icon = item.icon || resolveLucideIcon(item.iconName) || LucideIcons.HelpCircle;
                      return <Icon className="w-8 h-8 stroke-[1.5]" />;
                    })()}
                  </div>
                  <h4 className="text-xl font-medium mb-3 text-text-heading shrink-0">{item.title}</h4>
                  <p className="text-text-body text-sm leading-relaxed flex-grow">{item.description}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={0.4}>
            <div className={`p-8 md:p-12 rounded-3xl shadow-sm border ${getThemeClass('themes', 'bg-secondary-colonial-white text-text-heading border-secondary-saffron-mango/20')}`} style={getThemeStyle('themes')}>
              <h4 className="text-2xl font-medium mb-8">{siteContent?.themesTitle || 'Selected Themes'}</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {combinedThemes.map((theme, idx) => (
                  <div key={idx} className="border-t border-brand-sap/10 pt-6 flex flex-col h-full">
                    <div className="flex items-center space-x-3 mb-3 shrink-0">
                      {(() => {
                        const Icon = theme.icon || resolveLucideIcon(theme.iconName) || LucideIcons.Briefcase;
                        return <Icon className="w-8 h-8 stroke-[1.5] text-brand-sap mb-2" />;
                      })()}
                      <h5 className="text-xl font-medium">{theme.title}</h5>
                    </div>
                    <p className="text-text-body text-sm leading-relaxed flex-grow">{theme.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className={`py-16 relative ${getThemeClass('team', 'bg-bg-pearl')}`} style={getThemeStyle('team')}>
        <div className="max-w-7xl mx-auto px-6 text-center">
          <FadeIn>
            <div className="mb-12">
              <h2 className="text-[10px] font-bold tracking-[0.2em] uppercase text-brand-pea mb-2">{siteContent?.teamLabel || 'Team'}</h2>
              <h3 className="text-2xl md:text-3xl font-heading text-text-heading mb-4">
                {siteContent?.teamTitle || 'The Team behind VEQ'}
              </h3>
              <p className="text-brand-sap/70 text-sm max-w-xl mx-auto">
                {siteContent?.teamSubtitle || "We've been in the same shoes as the companies we invest in"}
              </p>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 max-w-6xl mx-auto text-left">
            {combinedTeam.map((member, idx) => (
              <FadeIn key={idx} delay={idx * 0.1}>
                <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-center md:items-start bg-white/60 p-6 md:p-8 rounded-3xl border border-gray-100 shadow-sm hover:border-brand-sap/20 hover:shadow-lg transition-all duration-300 w-full min-h-full">
                  {/* Image Container */}
                  <div className="relative w-36 h-48 md:w-44 md:h-56 rounded-2xl overflow-hidden shadow-sm shrink-0 border border-bg-grey-green/35 bg-bg-grey-green/5">
                    <img 
                      src={member.image} 
                      alt={member.name} 
                      className="w-full h-full object-cover object-center transition-transform duration-700 hover:scale-[1.03]"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                  
                  {/* Content Container */}
                  <div className="flex flex-col flex-grow min-w-0 pt-1 text-center md:text-left">
                    <div className="mb-3">
                      <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                        {member.linkedin ? (
                          <a 
                            href={member.linkedin} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="group/name inline-flex items-center gap-1.5 hover:text-brand-sap transition-colors"
                          >
                            <h4 className="text-[19px] font-heading font-medium text-text-heading group-hover/name:text-brand-sap transition-colors">{member.name}</h4>
                            <Linkedin className="w-4 h-4 text-brand-sap/70 group-hover/name:text-brand-sap transition-colors shrink-0" />
                          </a>
                        ) : (
                          <h4 className="text-[19px] font-heading font-medium text-text-heading">{member.name}</h4>
                        )}
                      </div>
                      <p className="text-brand-sap font-bold text-[10px] uppercase tracking-[0.14em] mt-0.5">{member.role}</p>
                    </div>
                    
                    <p className="text-text-body text-[13px] md:text-sm leading-relaxed text-left font-normal whitespace-pre-wrap">
                      {renderBioWithLinks(member.bio)}
                    </p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Coaches Section */}
      <section id="coaches" className={`py-24 relative ${getThemeClass('coaches', 'bg-white')}`} style={getThemeStyle('coaches')}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mb-16">
            <div className="lg:col-span-5">
              <FadeIn>
                <h2 className="text-sm font-bold tracking-widest uppercase text-brand-pea mb-4">{siteContent?.coachesLabel || 'Advisors'}</h2>
                <h3 className="text-4xl md:text-5xl font-heading leading-tight mb-6 text-text-heading">
                  {siteContent?.coachesTitle || 'Our coaches'}
                </h3>
                <p className="text-lg text-text-body leading-relaxed">
                  {siteContent?.coachesDescription || "15 handpicked specialists matched to each company's needs. Not advisors on paper — operators who roll up their sleeves."}
                </p>
              </FadeIn>
            </div>
            
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6 content-start">
              {combinedCoaches.map((area, idx) => (
                <FadeIn key={idx} delay={idx * 0.1} className="h-full">
                  <div className="bg-bg-merino p-8 rounded-2xl h-full border border-brand-tea/10 hover:shadow-md transition-shadow">
                    <div className="text-brand-sap/60 mb-4">
                      {(() => {
                        const Icon = area.icon || resolveLucideIcon(area.iconName) || LucideIcons.HelpCircle;
                        return <Icon className="w-8 h-8 stroke-[1.5]" />;
                      })()}
                    </div>
                    <h4 className="text-xl font-medium mb-3 text-text-heading">{area.title}</h4>
                    <p className="text-text-body text-sm leading-relaxed">{area.description}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>

          <FadeIn delay={0.4}>
            <div className="bg-bg-beige py-6 md:py-8 rounded-3xl border border-brand-tea/20 text-center overflow-hidden relative">
              <h4 className="text-[10px] md:text-xs font-bold tracking-widest uppercase text-brand-pea mb-4 px-6 opacity-80">{siteContent?.coachesSubtitle || 'Our coaches come from'}</h4>
              
              {/* Marquee Container */}
              <div className="relative w-full overflow-hidden flex items-center group">
                {/* Edge Fades */}
                <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-bg-beige to-transparent z-10 pointer-events-none" />
                <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-bg-beige to-transparent z-10 pointer-events-none" />

                <div className="flex animate-marquee-slow w-max hover:[animation-play-state:paused] relative gap-x-6 md:gap-x-8">
                  {/* First set of logos */}
                  <div className="flex items-center gap-x-6 md:gap-x-8">
                    {combinedCoachLogos.map((item: any, idx: number) => {
                      const isVntrs = item.name.toUpperCase() === 'VNTRS';
                      const logoEl = (
                        <img 
                          src={item.logo} 
                          alt={item.name} 
                          width={item.width || 120}
                          height={item.height || 40}
                          style={getLogoStyles(item.name)}
                          className="w-auto h-auto object-contain opacity-95 hover:opacity-100 transition-all duration-300" 
                          title={item.name}
                        />
                       );
                      return (
                        <div key={`set-card1-${idx}`} className="flex items-center justify-center shrink-0">
                          <div className="bg-white h-14 md:h-18 w-36 md:w-48 px-4 py-2.5 rounded-2xl flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.03)] border border-brand-tea/10 hover:border-brand-tea/25 hover:shadow-md transition-all duration-300 hover:scale-105 select-none">
                            {isVntrs ? (
                              <a href="https://vntrs.com/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full h-full cursor-pointer">
                                {logoEl}
                              </a>
                            ) : (
                              logoEl
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Second set of logos for seamless loop */}
                  <div className="flex items-center gap-x-6 md:gap-x-8">
                    {combinedCoachLogos.map((item: any, idx: number) => {
                      const isVntrs = item.name.toUpperCase() === 'VNTRS';
                      const logoEl = (
                        <img 
                          src={item.logo} 
                          alt={item.name} 
                          width={item.width || 120}
                          height={item.height || 40}
                          style={getLogoStyles(item.name)}
                          className="w-auto h-auto object-contain opacity-95 hover:opacity-100 transition-all duration-300" 
                          title={item.name}
                        />
                       );
                      return (
                        <div key={`set-card2-${idx}`} className="flex items-center justify-center shrink-0">
                          <div className="bg-white h-14 md:h-18 w-36 md:w-48 px-4 py-2.5 rounded-2xl flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.03)] border border-brand-tea/10 hover:border-brand-tea/25 hover:shadow-md transition-all duration-300 hover:scale-105 select-none">
                            {isVntrs ? (
                              <a href="https://vntrs.com/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full h-full cursor-pointer">
                                {logoEl}
                              </a>
                            ) : (
                              logoEl
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Venture Studio Section */}
      <section id="venture-studio" className={`py-24 relative ${getThemeClass('pillars', 'bg-bg-pearl')}`} style={getThemeStyle('pillars')}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-5">
              <FadeIn>
                <h2 className="text-sm font-bold tracking-widest uppercase text-brand-pea mb-4">{siteContent?.studioLabel || 'Venture Studio'}</h2>
                <h3 className="text-4xl md:text-5xl font-heading leading-tight mb-6 text-text-heading flex items-center flex-wrap gap-x-3">
                  {getStudioTitle()} <a href="https://vntrs.com/" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-all cursor-pointer"><img src={vntrsLogo} alt="VNTRS" className="h-[0.85em] object-contain translate-y-[-2px] opacity-80" /></a>
                </h3>
                <p className="text-lg text-text-body leading-relaxed">
                  {siteContent?.studioDescription || "Our sister company and the leading venture studio in the Nordics. Hands-on operational support in tech, product, and growth."}
                </p>
              </FadeIn>
            </div>
            
            <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {currentVntrsStats.map((stat: any, idx: number) => (
                <FadeIn key={idx} delay={idx * 0.1} className="h-full">
                  <div className="bg-white p-6 rounded-2xl h-full border border-brand-tea/20 text-center flex flex-col justify-center hover:shadow-md transition-shadow">
                    <span className="text-3xl md:text-4xl font-heading text-brand-sap mb-2">{stat.value}</span>
                    <span className="text-xs font-bold tracking-widest uppercase text-text-muted">{stat.label}</span>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      <section id="news" className={`py-24 relative ${getThemeClass('news', 'bg-white')}`} style={getThemeStyle('news')}>
        <div className="max-w-7xl mx-auto px-6">
          <FadeIn>
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
              <div>
                <h2 className="text-sm font-bold tracking-widest uppercase text-brand-pea mb-4">{siteContent?.newsLabel || 'Latest News'}</h2>
                <h3 className="text-4xl md:text-5xl font-heading leading-tight text-text-heading">
                  {siteContent?.newsTitle || 'News & Updates'}
                </h3>
              </div>
              <div className="flex items-center space-x-6">
                <button onClick={() => setShowAllNews(true)} className="inline-flex items-center space-x-2 text-brand-sap font-medium hover:text-brand-pea transition-colors group">
                  <span>{siteContent?.newsAllButton || 'View all news'}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {NEWS.map((item, idx) => (
              <FadeIn key={idx} delay={idx * 0.1} className="h-full">
                <button onClick={() => setSelectedArticle(item)} className="group block h-full text-left w-full">
                  <div className="bg-bg-beige rounded-3xl overflow-hidden h-full flex flex-col border border-bg-grey-green hover:shadow-xl transition-all duration-500">
                    <div className="aspect-[16/10] overflow-hidden relative">
                      <img 
                        src={item.image} 
                        alt={item.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        referrerPolicy="no-referrer"
                      />
                      <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase text-brand-sap">
                        {item.category}
                      </div>
                    </div>
                    <div className="p-8 flex-1 flex flex-col">
                      <div className="text-text-muted text-sm mb-3">{item.date}</div>
                      <h4 className="text-xl font-medium text-text-heading mb-4 group-hover:text-brand-sap transition-colors line-clamp-3">
                        {item.title}
                      </h4>
                      <div className="mt-auto pt-4 flex items-center text-brand-sap font-medium text-sm">
                        <span>{siteContent?.readArticleLabel || 'Read article'}</span>
                        <ArrowUpRight className="w-4 h-4 ml-1 opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all" />
                      </div>
                    </div>
                  </div>
                </button>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA / Contact Section */}
      <section id="contact" className={`py-24 relative overflow-hidden ${getThemeClass('contact', 'text-white')}`} style={getThemeStyle('contact')}>
        {/* Background */}
        <div className="absolute inset-0 z-0">
          {siteContent?.contactBackgroundImage ? (
            siteContent.contactBackgroundImage.match(/\.(mp4|webm|mov|ogg)(\?.*)?$/i) || siteContent.contactBackgroundImage.includes('video/') ? (
              loadingComplete ? (
                <video 
                  ref={contactVideoRef}
                  src={siteContent.contactBackgroundImage} 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  preload="none"
                  className="w-full h-full object-cover scale-100"
                />
              ) : (
                <div className="w-full h-full bg-[#18181B]" />
              )
            ) : (
              <img 
                src={siteContent.contactBackgroundImage} 
                alt="Contact Background"
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
            )
          ) : (
            loadingComplete ? (
              <video 
                ref={contactVideoRef}
                src={backVideo}
                autoPlay 
                loop 
                muted 
                playsInline
                preload="none"
                className="w-full h-full object-cover scale-100"
              />
            ) : (
              <div className="w-full h-full bg-[#18181B]" />
            )
          )}
        </div>

        {(() => {
          const theme = siteContent?.appearance?.contact || 'dark';
          if (theme === 'custom') {
            return (
              <div 
                className="absolute inset-0 z-[5] opacity-90" 
                style={{ backgroundColor: siteContent?.appearance?.customBg_contact || '#000000' }}
              ></div>
            );
          }
          
          let overlayClass = 'bg-black/60';
          if (theme === 'light') overlayClass = 'bg-white/90';
          else if (theme === 'beige') overlayClass = 'bg-bg-beige/90';
          else if (theme === 'merino') overlayClass = 'bg-bg-merino/90';
          else if (theme === 'pearl') overlayClass = 'bg-bg-pearl/90';
          else if (theme === 'green') overlayClass = 'bg-brand-sap/90 text-white';
          
          return (
            <div className={`absolute inset-0 z-[5] ${overlayClass}`}></div>
          );
        })()}

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <FadeIn>
                {(() => {
                  const theme = siteContent?.appearance?.contact || 'dark';
                  const isLight = ['light', 'beige', 'merino', 'pearl'].includes(theme);
                  const isCustom = theme === 'custom';
                  // Simple heuristic: if custom and text is dark, it's light mode. But simpler is just white with borders.
                  let pillClass = "bg-white/10 border-white/20 text-current";
                  if (isLight) pillClass = "bg-black/5 border-black/10 text-current";
                  
                  return (
                    <div className={`inline-flex items-center space-x-2 backdrop-blur-md px-4 py-1.5 rounded-full text-sm font-medium mb-8 border shadow-lg ${pillClass}`}>
                      <span className="w-2 h-2 rounded-full bg-secondary-sun animate-pulse" />
                      <span className="drop-shadow-sm">{siteContent?.contactLabel || 'Contact'}</span>
                    </div>
                  );
                })()}
                <h2 className="text-4xl md:text-6xl font-heading leading-tight mb-8 drop-shadow-sm">
                  {siteContent?.contactTitle || 'Building a Swedish technology company with real traction?'}
                </h2>
                <p className="text-xl opacity-90 mb-10 max-w-lg drop-shadow-lg font-medium">
                  {siteContent?.contactText || 'We\'re always interested in meeting founders who share our values. Get in touch and let\'s take it from there.'}
                </p>
                <button 
                  onClick={() => setShowContactForm(true)}
                  className="inline-flex items-center justify-center space-x-3 text-brand-sap bg-brand-tea hover:bg-brand-granny-smith px-8 py-4 rounded-full font-medium transition-colors shadow-md group"
                >
                  <span>{siteContent?.contactMailButton || 'Email Us'}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </FadeIn>
            </div>
            
            <div className="lg:justify-self-end w-full max-w-md">
              <FadeIn delay={0.2}>
                <div className="bg-bg-beige p-8 rounded-3xl shadow-xl border border-bg-grey-green">
                  <h3 className="text-xl font-medium mb-6 text-text-heading">{siteContent?.contactOfficeHeading || 'Stockholm Office'}</h3>
                  <ul className="space-y-6">
                    <li className="flex items-start space-x-4">
                      <MapPin className="w-5 h-5 text-brand-pea mt-1" />
                      <div>
                        <p className="font-medium text-text-heading">{siteContent?.contactOfficeName || 'VEQ Capital'}</p>
                        <p className="text-text-body whitespace-pre-line">{siteContent?.footerAddress || 'Sankt Eriksgatan 63B\n112 34 Stockholm\nSweden'}</p>
                      </div>
                    </li>
                  </ul>
                  
                  <div className="w-full h-64 rounded-2xl overflow-hidden border bg-gray-100 border-bg-grey-green relative shadow-inner">
                    <iframe
                      className="absolute inset-0 w-full h-full"
                      style={{ border: 0 }}
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://maps.google.com/maps?q=${encodeURIComponent(siteContent?.contactMapQuery || 'Sankt Eriksgatan 63B, Stockholm')}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
                    ></iframe>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-bg-grey-green bg-bg-beige">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center">
          <div className="mb-4 md:mb-0">
            <img src={siteContent?.logoFooter || siteContent?.logoHeader || veqLogo} alt="VEQ" className="h-8 object-contain" />
          </div>
          <div className="flex items-center space-x-6 mb-4 md:mb-0">
            <button 
              onClick={() => setShowSustainability(true)}
              className="text-sm text-gray-700 hover:text-brand-sap transition-colors"
            >
              {siteContent?.footerSustainabilityLink || 'SFDR Website Disclosure'}
            </button>
            <a 
              href="https://se.linkedin.com/company/veq-vc" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-flex items-center text-sm text-gray-700 hover:text-brand-sap gap-1 transition-colors"
            >
              <Linkedin className="w-4 h-4 ml-1" />
              <span>LinkedIn</span>
            </a>
          </div>
          <div className="text-sm text-gray-700">
            {siteContent?.footerCopyright || `© ${new Date().getFullYear()} VEQ Capital. All rights reserved.`}
          </div>
        </div>
      </footer>

      <AnimatePresence>
        {showContactForm && (
          <ContactForm 
            onClose={() => setShowContactForm(false)} 
            recipientEmail={siteContent?.footerEmail || "contact@veq.capital"}
            title={siteContent?.contactFormTitle}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!loadingComplete && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className="fixed inset-0 z-[10000] flex flex-col items-center justify-center bg-[#EAE8E4] select-none"
          >
            {/* Subtle radial ambient background glow for depth */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.25)_0%,transparent_70%)] pointer-events-none" />
            
            <div className="relative flex flex-col items-center">
              {/* Logo container with elegant custom entrance */}
              <motion.div
                initial={{ opacity: 0, scale: 0.97, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="flex flex-col items-center"
              >
                <img 
                  src={veqLogo} 
                  alt="VEQ" 
                  className="h-11 w-auto object-contain mb-8 filter drop-shadow-[0_2px_8px_rgba(0,0,0,0.015)]" 
                />
              </motion.div>

              {/* Loader and tagline with sequenced fade-in */}
              <motion.div 
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.7, ease: "easeOut" }}
                className="flex flex-col items-center gap-4"
              >
                {/* Sleek, thin minimalist hairline progress bar */}
                <div className="w-48 h-[1.5px] bg-brand-sap/12 rounded-full overflow-hidden relative">
                  <motion.div 
                    className="absolute top-0 bottom-0 bg-brand-sap rounded-full"
                    initial={{ left: "-30%", width: "30%" }}
                    animate={{ left: ["-30%", "100%"] }}
                    transition={{
                      repeat: Infinity,
                      duration: 1.5,
                      ease: [0.25, 1, 0.5, 1]
                    }}
                  />
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}